import { recordCameraStatusSet } from '@streem/analytics';
import { isLocalVideoTrack, } from '@streem/sdk-types';
import { invariant } from '@streem/toolbox';
import { of, race, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { CameraSubject } from 'streem-sdk-protobuf';
import { logger } from '../../../util/logger';
import { filterNull } from '../../../util/rx/filter_null';
import { takeFirstEmitted } from '../../../util/rx/take_first_emitted';
import { DefaultConnectedStrategy } from './connected_strategy';
import { generateStreemVideoTrackConstraints } from '../../../util/track_constraints';
const MIN_TIME_BETWEEN_TRACK_RESTARTS_MS = 10 * 1000; // 10 seconds
export class VideoController {
    constructor(config) {
        this.config = config;
        this.log = logger.extend('sdk:video_controller');
        this.destroyed = new Subject();
        // We use this to track the state we use to pass to the video track since it is not guaranteed to
        // exist when we call the constructor and connect()
        this.backgroundedYn = false;
        this.id = config.id;
        this.ownerIsLocalUser = config.owner.isLocalUser;
        this.ownerId = config.owner.id;
        this.videoTrackWallItem = this.config.wallItem.pipe(shareReplay(1));
        this.lastTrackRestart = Date.now();
        /**
         * Listen for the latest video track
         * and set it as a property on this class.
         */
        this.config.track
            .pipe(takeUntil(this.destroyed))
            .subscribe((v) => {
            this.videoTrack = v;
            // Handle the condition where `connect()` is called before the remote track exists
            v?.setBackgrounded && v.setBackgrounded(this.backgroundedYn);
        });
    }
    connect() {
        this.cameraSubject = this.videoTrackWallItem.pipe(
        // Using `0` (World) is Replicating Protobuf behavior/response with missing cameraSubject
        map(item => {
            const cameraSubject = item?.cameraSubject ?? CameraSubject.CAMERA_SUBJECT_WORLD;
            // update internally so track restart can always reference the current value
            this._cameraSubject = cameraSubject;
            return cameraSubject;
        }), filterNull(), shareReplay(1));
        this.muted = this.buildMutedStatus(this.videoTrackWallItem);
        this.enabled = this.buildEnabledStatus(this.config.track);
        this.started = this.buildStartedStatus(this.config.track);
        this.connected = this.buildConnectedStatus(this.config.track);
        this.backgrounded = this.buildBackgroundedStatus(this.videoTrackWallItem);
        this.backgrounded.subscribe(backgrounded => {
            this.backgroundedYn = backgrounded;
            this.videoTrack?.setBackgrounded &&
                this.videoTrack.setBackgrounded(this.backgroundedYn);
        });
        this.paused = this.buildPausedStatus(this.config.track);
        this.updateVideoTrackWallItemOnTrackUpdates();
    }
    destroy() {
        this.destroyed.next(undefined);
    }
    setConnectedStrategy(strategy) {
        this.connectedStrategy = strategy;
    }
    startRenderingVideoFrames(callback) {
        let stopFrameScheduler;
        const connectedSubscription = this.connected.subscribe({
            next: value => {
                if (stopFrameScheduler) {
                    stopFrameScheduler();
                    stopFrameScheduler = undefined;
                }
                if (value) {
                    invariant(this.videoTrack, 'Missing internal videoTrack');
                    stopFrameScheduler = this.videoTrack.scheduleRenderingCallback(callback);
                }
            },
        });
        return () => {
            if (connectedSubscription) {
                connectedSubscription.unsubscribe();
            }
            if (stopFrameScheduler) {
                stopFrameScheduler();
            }
        };
    }
    setEnabled(enabled) {
        if (this.ownerIsLocalUser && this.videoTrack && isLocalVideoTrack(this.videoTrack)) {
            this.videoTrack.setEnabled(enabled);
            recordCameraStatusSet(enabled, this.config.roomId);
            this.log.info(`Track of type: video has been turned ${enabled ? 'ON' : 'OFF'} for participant: ${this.ownerId.toString()}`);
        }
    }
    updateVideoTrackWallItemOnTrackUpdates() {
        race(this.destroyed.asObservable())
            .pipe(take(1))
            .subscribe(() => {
            if (this.ownerIsLocalUser) {
                this.config.roomWall.delete(this.id);
            }
            this.config.onDestroy(this);
        });
        /**
         * Whenever we update our enabled state, update the VideoTrack wall item for the local user
         * when the user mutes / unmutes their video.
         */
        this.enabled
            .pipe(withLatestFrom(this.connected, this.videoTrackWallItem), takeUntil(this.destroyed))
            .subscribe(([enabled, connected, wallItem]) => {
            if (connected && this.ownerIsLocalUser) {
                this.config.roomWall.addOrUpdate(this.config.roomWall.builder.buildTrackWallItem('videoTrack', {
                    name: wallItem?.name,
                    cameraSubject: wallItem?.cameraSubject,
                    active: enabled,
                    backgrounded: false,
                }));
            }
        });
    }
    buildEnabledStatus(track) {
        return track.pipe(switchMap(track => !track
            ? of(false)
            : track.enabled.pipe(tap((e) => this.log.info(`Track of type: video has been ${e ? 'enabled' : 'disabled'} for participant: ${this.ownerId.toString()}`)))), shareReplay(1));
    }
    buildMutedStatus(videoTrackWallItem) {
        return videoTrackWallItem.pipe(map(item => !item?.active), shareReplay(1));
    }
    buildBackgroundedStatus(videoTrackWallItem) {
        return videoTrackWallItem.pipe(map(item => !!item?.backgrounded), tap(backgrounded => this.log.info(`Track of type: video has been ${backgrounded ? 'backgrounded' : 'foregrounded'} for participant: ${this.ownerId.toString()}`)), shareReplay(1));
    }
    buildStartedStatus(track) {
        return track.pipe(switchMap(track => !track
            ? of(false)
            : track.started.pipe(tap((s) => this.log.info(`Track of type: video has ${s ? 'started' : 'stopped'} for participant: ${this.ownerId.toString()}`)))), shareReplay(1));
    }
    buildConnectedStatus(track) {
        if (!this.connectedStrategy) {
            this.connectedStrategy = new DefaultConnectedStrategy(track);
        }
        return this.connectedStrategy.createConnected().pipe(tap(connected => this.log.info(`Track of type: video has been ${connected ? 'connected' : 'disconnected'} for participant: ${this.ownerId.toString()}`)), shareReplay(1));
    }
    buildPausedStatus(track) {
        // paused is an optional property on the track.  As of the writing of this code, it is only
        //    used for the remote track in Chime.
        return track.pipe(switchMap(track => track && track.paused
            ? track.paused.pipe(tap((s) => this.log.info(`Track of type: video has ${s ? 'paused' : 'unpaused'} for participant: ${this.ownerId.toString()}`)))
            : of(false)), shareReplay(1));
    }
    async setBackgrounded(backgrounded) {
        const wallItem = await takeFirstEmitted(this.videoTrackWallItem);
        this.config.roomWall.update(this.config.roomWall.builder.buildTrackWallItem('videoTrack', {
            ...wallItem,
            backgrounded,
        }));
        this.log.info(`videoTrack backgrounded value set to backgrounded=${backgrounded} for userId=${this.config.owner.id.toString()}`);
    }
    async flipCamera(newSubject) {
        invariant(this.ownerIsLocalUser, "Attempted to flip the camera for a video feed the user doesn't own. Did you mean to request the flip?");
        invariant(isLocalVideoTrack(this.videoTrack), 'Attempted to flip camera for a video track that does not support it');
        const wallItem = await takeFirstEmitted(this.videoTrackWallItem);
        const defaultConstraints = await generateStreemVideoTrackConstraints(newSubject);
        let constraints = this.videoTrack.getMediaTrackConstraints();
        // prevent old track 'facingMode' constraint value from overriding new constraint 'facingMode' value
        constraints = {
            ...defaultConstraints,
            width: constraints?.width || defaultConstraints.width,
            height: constraints?.height || defaultConstraints.height,
        };
        const successful = await this.videoTrack.restart(constraints);
        if (successful) {
            this.config.roomWall.update(this.config.roomWall.builder.buildTrackWallItem('videoTrack', {
                ...wallItem,
                cameraSubject: newSubject,
            }));
        }
        return successful;
    }
    async restart(constraints) {
        invariant(this.ownerIsLocalUser, 'Attempted to restart a remote video track you do not control. Did you mean to restart a local track instead?');
        invariant(isLocalVideoTrack(this.videoTrack), 'Attempted to restart a remote video track. Did you mean to call this on the local video track?');
        const defaultConstraints = await generateStreemVideoTrackConstraints(this._cameraSubject);
        // override default constraints if the existing track has any previously applied constraints
        let activeConstraints = this.videoTrack.getMediaTrackConstraints();
        activeConstraints = {
            ...defaultConstraints,
            ...constraints,
            width: activeConstraints?.width ?? constraints?.width ?? defaultConstraints.width,
            height: activeConstraints?.height ?? constraints?.height ?? defaultConstraints.height,
        };
        return await this.videoTrack.restart(activeConstraints);
    }
    async handlePoorQualityRestart() {
        // rate-limit the video track restarts.
        if (this.lastTrackRestart >= Date.now() - MIN_TIME_BETWEEN_TRACK_RESTARTS_MS) {
            this.log.warn(`Local video track quality fell below threshold, but restart rate-limit of once per ${MIN_TIME_BETWEEN_TRACK_RESTARTS_MS} ms has been exceeded.`);
            return;
        }
        const successful = await this.restart();
        this.lastTrackRestart = Date.now();
        if (successful) {
            this.log.info(`Local video track quality fell below threshold and was restarted successfully for participant with userId=${this.ownerId.toString()}`);
        }
        else {
            this.log.warn(`Local video track quality fell below threshold but failed to restart for participant with userId=${this.ownerId.toString()}`);
        }
    }
    async handleBackgroundRestart() {
        const successful = await this.restart();
        if (successful) {
            this.log.info(`Local video track was backgrounded and was restarted successfully for participant with userId=${this.ownerId.toString()}`);
        }
        else {
            this.log.warn(`Local video track was backgrounded but failed to restart for participant with userId=${this.ownerId.toString()}`);
        }
    }
}
//# sourceMappingURL=video_controllers.js.map